import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

import { NeuralAnimation } from "../NeuralAnimation";

/**
 * The landing sequence.
 *
 * Data enters the input layer and propagates forward through the network,
 * then the overlay lifts into the site. It shares its drawing with every
 * loading state on the site (`NeuralAnimation`), so the intro and the spinner
 * are visibly the same object rather than two unrelated ideas.
 *
 * Paced to be *watchable*. The first version ran the whole forward pass in
 * 2.2s, which is long enough to notice and too short to read — the signal had
 * crossed the output layer before the eye had finished the input one. A
 * propagation animation nobody can follow is just a flash.
 *
 * Four rules keep it from being the thing everybody hates about intros:
 *
 *   **It plays once per session**, not per navigation. A splash screen on
 *   every route change is a tax on the people who visit most.
 *
 *   **Skipping is a real focusable button** — plus Escape — not a
 *   click-anywhere handler a keyboard user cannot reach.
 *
 *   **It never blocks the page.** The homepage renders underneath the whole
 *   time. This is an overlay that lifts, not a gate that opens, so a stall or
 *   a JS error leaves a working site rather than a locked screen.
 *
 *   **Reduced motion skips it entirely.** Not "plays faster" — a full-screen
 *   propagating animation is precisely what that preference asks not to see.
 */

const SEEN_KEY = "intro-seen";

/** Seconds per layer. Slow enough to watch the signal cross each one. */
const LAYER_DURATION = 0.78;
/** The sweep, plus a beat to read the caption before it lifts. */
const TOTAL_MS = 4600;

/**
 * Decided once, on the first render, and recorded immediately.
 *
 * Lazy initial state rather than an effect: the answer is available
 * synchronously, so deferring it would render one frame of "no intro" and
 * then cascade a re-render into it — a visible flash of the homepage before
 * the overlay drops over it, which is worse than no intro at all.
 *
 * Writing the session flag here also makes StrictMode's double-invocation
 * harmless: the second call reads the flag the first one set and returns false.
 */
function shouldPlayIntro(reduced: boolean): boolean {
  if (reduced) return false;
  try {
    if (sessionStorage.getItem(SEEN_KEY) === "1") return false;
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Storage disabled — skip rather than replay on every navigation.
    return false;
  }
  return true;
}

export function TrainingIntro() {
  const reduced = useReducedMotion() ?? false;
  const [playing, setPlaying] = useState(() => shouldPlayIntro(reduced));

  useEffect(() => {
    if (!playing) return;
    const done = setTimeout(() => {
      setPlaying(false);
    }, TOTAL_MS);
    return () => {
      clearTimeout(done);
    };
    // Mount-only: re-running when `playing` flips to false would schedule a
    // timeout that does nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!playing) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setPlaying(false);
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [playing]);

  return (
    <AnimatePresence>
      {playing && (
        <motion.div
          // NOT aria-hidden. It contains a focusable Skip button, and
          // `aria-hidden` around a focusable control is the aria-hidden-focus
          // violation — a keyboard user can Tab to something a screen reader
          // has been told does not exist. The artwork carries aria-hidden;
          // the button and the caption stay real.
          initial={{ opacity: 1 }}
          // Opacity only on the overlay itself. Scaling a `fixed inset-0`
          // element past 1 makes it wider than the viewport and gives the
          // whole document a horizontal scrollbar for the length of the exit
          // — caught at 360px, where 1.05 is 18px of overflow. The contents
          // scale instead; the full-bleed layer never changes size.
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden bg-canvas"
        >
          {/* A wash behind the network, so the overlay reads as lit rather
              than as a flat panel the graph sits on. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(45%_45%_at_50%_45%,color-mix(in_oklab,var(--color-glow)_18%,transparent),transparent_72%)]"
          />

          <motion.div
            exit={{ scale: 1.08, filter: "blur(10px)" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex flex-col items-center"
          >
            <NeuralAnimation
              layerDuration={LAYER_DURATION}
              className="w-[min(38rem,86vw)]"
            />

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="relative mt-8 font-mono text-2xs tracking-[0.34em] text-text-dim uppercase"
            >
              Iftikhar Ali · AI Engineer
            </motion.p>

            {/* Held back until the sequence is well underway, so it does not
              read as "this is going to take a while". */}
            <motion.button
              type="button"
              onClick={() => {
                setPlaying(false);
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 0.5 }}
              className="relative mt-12 rounded-full border border-edge px-5 py-2 text-2xs text-text-dim transition-colors duration-300 hover:border-accent-dim hover:text-text"
            >
              Skip
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
